/**
 * Shopify cart image loader/recovery.
 * Configure it with window.CartImageLoaderConfig before loading this asset.
 */
(function cartImageLoader(window, document) {
  'use strict';

  const defaults = {
    // Theme selectors
    itemSelector: '.cart-item',
    imageSelector: 'img',
    imageMountSelector: '.cart-item__media', // null, inserted as first element in the cart listed item markup

    // Polling
    cartEndpoint: '/cart.js',
    intervalMs: 2500,
    maxRecoveryMs: 45000,
    fetchOptions: { credentials: 'same-origin' },

    // Placeholder
    placeholderWidth: 120,
    placeholderHeight: 90,
    placeholderBackground: '#f3f3f3',
    placeholderForeground: '#8c8c8c',
    placeholderClass: 'cart-image-loader__placeholder',
    placeholderLabel: 'Product image loading',
    // Grace period before a spinner is shown; small/cached thumbnails finish first.
    placeholderDelayMs: 600,

    // Recovered <img>
    recoveredImageClass: 'cart-image-loader__image',
    recoveredImageAlt: '',
    insertRecoveredImages: true,
    recoverExistingImages: false,
    matchPlaceholderDimensions: true,
    recoveredImageObjectFit: 'cover',

    // Cart row matching
    itemKeyAttribute: 'data-cart-item-key',
    lineAttribute: 'data-cart-item-index',
    variantIdAttribute: 'data-variant-id',
    productIdAttribute: 'data-product-id',
    useIndexFallback: true,

    // Lifecycle
    observeCartChanges: true,
    observeRoot: null,
    reloadPageOnRecovery: false,
    logging: false,
    autoStart: true,

    // Optional hooks. Set these to functions for theme-specific behavior.
    getCartItemForElement: null,
    getImageUrl: (cartItem) => {
      if (!cartItem) return '';
      if (typeof cartItem.image === 'string') return cartItem.image;
      return cartItem.featured_image && cartItem.featured_image.url
        ? cartItem.featured_image.url
        : '';
    },
    transformImageUrl: (url) => {
      try {
        const image = new URL(url, window.location.origin);
        if (/\/(cdn\/shop|s\/files)\//.test(image.pathname)) image.searchParams.set('width', '320');
        return image.href;
      } catch (_) { return url; }
    },
    onRecovered: null
  };

  const config = Object.assign({}, defaults, window.CartImageLoaderConfig || {});
  const placeholderMarker = 'data-cart-image-loader-placeholder';
  const hiddenMarker = 'data-cart-image-loader-hidden';
  const listenerMarker = 'data-cart-image-loader-listener';
  const recoveredMarker = 'data-cart-image-loader-recovered';
  const hiddenImageStyles = new WeakMap();
  const graceTimers = new WeakMap();
  let timerId = null;
  let observerTimer = null;
  let observer = null;
  let requestInProgress = false;
  let started = false;
  let recoveryStarted = 0;
  let rateLimitedUntil = 0;
  let exhausted = false;

  const log = (...messages) => {
    if (config.logging) console.log('[Cart image loader]', ...messages);
  };

  const warn = (...messages) => {
    if (config.logging) console.warn('[Cart image loader]', ...messages);
  };

  const escapeXml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

  const imageIsLoaded = (image) => Boolean(
    image && image.complete && image.naturalWidth > 0 && image.naturalHeight > 0
  );

  const findImage = (item) => item.querySelector(config.imageSelector)
    || item.querySelector(`[${recoveredMarker}]`);

  const applyImageDimensions = (image) => {
    if (!image || !config.matchPlaceholderDimensions) return;

    const width = Math.max(1, Number(config.placeholderWidth) || 120);
    const height = Math.max(1, Number(config.placeholderHeight) || 90);
    image.width = width;
    image.height = height;
    image.style.width = `${width}px`;
    image.style.height = `${height}px`;
    image.style.objectFit = config.recoveredImageObjectFit;
  };

  const createPlaceholder = () => {
    const element = document.createElement('span');
    const width = Math.max(1, Number(config.placeholderWidth) || 120);
    const height = Math.max(1, Number(config.placeholderHeight) || 90);
    const radius = Math.max(10, Math.round(Math.min(width, height) * 0.36));
    const background = escapeXml(config.placeholderBackground);
    const foreground = escapeXml(config.placeholderForeground);

    element.className = config.placeholderClass;
    element.setAttribute(placeholderMarker, '');
    element.setAttribute('role', 'img');
    element.setAttribute('aria-label', config.placeholderLabel);
    element.style.display = 'inline-block';
    element.style.width = `${width}px`;
    element.style.height = `${height}px`;
    element.style.flex = `0 0 ${width}px`;
    element.style.lineHeight = '0';
    element.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"
           viewBox="0 0 ${width} ${height}" aria-hidden="true" focusable="false">
        <rect width="100%" height="100%" rx="3" fill="${background}"/>
        <circle cx="${width / 2}" cy="${height / 2}" r="${radius}"
                fill="none" stroke="${foreground}" stroke-width="3"
                stroke-linecap="round" stroke-dasharray="${radius * 1.4} ${radius * 5}"
                opacity=".7">
          <animateTransform attributeName="transform" type="rotate"
                            from="0 ${width / 2} ${height / 2}"
                            to="360 ${width / 2} ${height / 2}"
                            dur="1s" repeatCount="indefinite"/>
        </circle>
      </svg>`;

    return element;
  };

  const hideImage = (image) => {
    if (!image || image.hasAttribute(hiddenMarker)) return;
    image.loading = 'eager';

    hiddenImageStyles.set(image, {
      visibility: image.style.visibility,
      position: image.style.position,
      pointerEvents: image.style.pointerEvents
    });
    image.setAttribute(hiddenMarker, '');

    // Do not use display:none here. Browsers may postpone a lazy-loaded image
    // indefinitely when it has no rendered box, leaving the placeholder stuck.
    image.style.visibility = 'hidden';
    image.style.position = 'absolute';
    image.style.pointerEvents = 'none';
  };

  const restoreImage = (image) => {
    if (!image || !image.hasAttribute(hiddenMarker)) return;

    const oldStyles = hiddenImageStyles.get(image) || {};
    image.style.visibility = oldStyles.visibility || '';
    image.style.position = oldStyles.position || '';
    image.style.pointerEvents = oldStyles.pointerEvents || '';
    hiddenImageStyles.delete(image);
    image.removeAttribute(hiddenMarker);
  };

  const removePlaceholder = (item, loadedImage = null) => {
    if (graceTimers.has(item)) {
      window.clearTimeout(graceTimers.get(item));
      graceTimers.delete(item);
    }
    item.querySelectorAll(`[${placeholderMarker}]`).forEach((element) => element.remove());
    restoreImage(loadedImage || findImage(item));
  };

  const showPlaceholder = (item) => {
    if (exhausted) return;
    if (item.querySelector(`[${placeholderMarker}]`)) return;

    const image = findImage(item);
    const mount = config.imageMountSelector
      ? item.querySelector(config.imageMountSelector)
      : (image ? image.parentElement : item);

    if (!mount) {
      warn('No image mount element found for:', item);
      return;
    }

    hideImage(image);
    mount.insertBefore(createPlaceholder(), image || mount.firstChild);
  };

  const listenForImageResult = (image, item) => {
    if (!image.hasAttribute(listenerMarker)) {
      image.setAttribute(listenerMarker, '');

      image.addEventListener('load', () => {
        if (imageIsLoaded(image)) {
          removePlaceholder(item, image);
          checkState();
        }
      });
      image.addEventListener('error', () => {
        if ((image.getAttribute('src') || '').startsWith('data:')) {
          image.setAttribute('data-ih-mimeeq-render-failed', '');
        }
        showPlaceholder(item);
        checkState();
      });
    }

    // The image may finish between scan() and listener registration, especially
    // when it is already in the browser cache.
    if (imageIsLoaded(image)) removePlaceholder(item, image);
  };

  const scan = () => {
    const missingItems = [];

    document.querySelectorAll(config.itemSelector).forEach((item, index) => {
      const image = findImage(item);
      const saved = typeof window.IHMimeeqCart?.thumbnailFor === 'function' && window.IHMimeeqCart.thumbnailFor(
        item.getAttribute(config.variantIdAttribute));
      if (image && saved && !image.hasAttribute('data-ih-mimeeq-render-failed') &&
          !imageIsLoaded(image) && image.getAttribute('src') !== saved) {
        listenForImageResult(image, item);
        image.loading = 'eager';
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.setAttribute('data-ih-mimeeq-render', 'local');
        image.src = saved;
      }
      applyImageDimensions(image);
      if (imageIsLoaded(image)) {
        removePlaceholder(item);
      } else {
        // Give the (usually tiny) image a grace window to load before showing a
        // spinner, so no placeholder flashes for images that load quickly.
        if (image) listenForImageResult(image, item);
        if (!image) {
          showPlaceholder(item);
        } else if (!graceTimers.has(item) && !exhausted) {
          const graceDelay = Math.max(0, Number(config.placeholderDelayMs) || 0);
          graceTimers.set(item, window.setTimeout(function () {
            graceTimers.delete(item);
            if (item.isConnected && !exhausted && !imageIsLoaded(image)) showPlaceholder(item);
          }, graceDelay));
        }

        // A present image may simply still be loading. By default, only a
        // completely absent <img> triggers /cart.js polling and recovery.
        // An image with a source is downloading already. Do not poll the cart
        // and keep assigning its URL while a slow mobile download is in flight.
        const source = image && (image.getAttribute('src') || image.getAttribute('srcset'));
        if (!image || (config.recoverExistingImages && (!source || image.complete))) {
          missingItems.push({ item, image, index });
        }
      }
    });

    return missingItems;
  };

  const findByAttribute = (element, attribute, cartItems, property) => {
    const value = element.getAttribute(attribute);
    if (value === null || value === '') return null;
    return cartItems.find((cartItem) => String(cartItem[property]) === String(value)) || null;
  };

  const resolveCartItem = (element, cartItems, domIndex) => {
    if (typeof config.getCartItemForElement === 'function') {
      const customMatch = config.getCartItemForElement(element, cartItems, domIndex);
      if (customMatch) return customMatch;
    }

    const keyMatch = findByAttribute(element, config.itemKeyAttribute, cartItems, 'key');
    if (keyMatch) return keyMatch;

    const line = Number(element.getAttribute(config.lineAttribute));
    if (Number.isInteger(line) && line > 0 && cartItems[line - 1]) return cartItems[line - 1];

    const variantMatch = findByAttribute(
      element, config.variantIdAttribute, cartItems, 'variant_id'
    );
    if (variantMatch) return variantMatch;

    const productMatch = findByAttribute(
      element, config.productIdAttribute, cartItems, 'product_id'
    );
    if (productMatch) return productMatch;

    return config.useIndexFallback ? (cartItems[domIndex] || null) : null;
  };

  const recoverImage = (missingEntry, cartItems) => {
    const { item, index } = missingEntry;
    const cartItem = resolveCartItem(item, cartItems, index);
    const originalUrl = config.getImageUrl(cartItem, item, index);
    if (!originalUrl) return false;

    const imageUrl = config.transformImageUrl(originalUrl, cartItem, item, index);
    if (!imageUrl) return false;

    let image = findImage(item);
    if (!image && config.insertRecoveredImages) {
      image = document.createElement('img');
      image.setAttribute(recoveredMarker, '');
      image.className = config.recoveredImageClass;
      image.alt = config.recoveredImageAlt || (cartItem.product_title || '');
      hideImage(image);

      const mount = config.imageMountSelector
        ? item.querySelector(config.imageMountSelector)
        : item;
      if (!mount) return false;
      mount.insertBefore(image, mount.firstChild);
    }

    if (!image) return false;
    applyImageDimensions(image);
    listenForImageResult(image, item);
    image.loading = 'eager';
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    // A failed CDN image can be retried on a later check; an in-flight one is
    // left alone so it can finish even on a slow connection.
    if (image.getAttribute('src') !== imageUrl || image.complete) image.src = imageUrl;
    return true;
  };

  const stopTimer = () => {
    if (timerId !== null) window.clearTimeout(timerId);
    timerId = null;
  };

  const scheduleCheck = (delay = config.intervalMs) => {
    if (!started || timerId !== null || requestInProgress) return;
    timerId = window.setTimeout(() => {
      timerId = null;
      getCartData();
    }, Math.max(0, Number(delay) || 0));
  };

  const checkState = () => {
    if (!started) return;
    const missingItems = scan();
    if (missingItems.length === 0) {
      stopTimer();
      log('No cart image elements are missing; polling is stopped.');
    } else if (!exhausted) {
      scheduleCheck();
    }
  };

  const getCartData = async () => {
    if (!started || requestInProgress || exhausted) return;
    if (Date.now() < rateLimitedUntil) { stopTimer(); return; }

    const missingBeforeRequest = scan();
    if (missingBeforeRequest.length === 0) {
      stopTimer();
      return;
    }

    requestInProgress = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    log('Checking cart data...');

    try {
      if (!recoveryStarted) recoveryStarted = Date.now();
      if (Date.now() - recoveryStarted >= config.maxRecoveryMs) { exhausted = true; return; }
      const response = await window.fetch(config.cartEndpoint, {...config.fetchOptions, signal:controller.signal});
      if (response.status === 429) {
        const seconds = Number(response.headers.get('Retry-After')) || 120;
        rateLimitedUntil = Date.now() + Math.max(60000, Math.min(seconds * 1000, 300000));
        exhausted = true;
        return;
      }
      if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);

      const cart = await response.json();
      const cartItems = Array.isArray(cart.items) ? cart.items : [];
      const recovered = missingBeforeRequest.filter((entry) => recoverImage(entry, cartItems));

      if (recovered.length > 0) {
        log(`Reloading ${recovered.length} missing cart image(s).`);
        if (typeof config.onRecovered === 'function') {
          config.onRecovered({ cart, missingItems: recovered });
        }
        if (config.reloadPageOnRecovery) window.location.reload();
      }
    } catch (error) {
      warn('Cart refresh check failed:', error);
    } finally {
      window.clearTimeout(timeout);
      requestInProgress = false;
      if (exhausted) document.querySelectorAll(config.itemSelector).forEach(item => removePlaceholder(item));
      checkState();
    }
  };

  const start = () => {
    if (started) return;
    started = true;

    if (config.observeCartChanges && window.MutationObserver) {
      const root = config.observeRoot || document.body;
      if (root) {
        observer = new MutationObserver(() => {
          if (observerTimer !== null) return;
          observerTimer = window.setTimeout(() => {
            observerTimer = null;
            const current = [...root.querySelectorAll(config.itemSelector)];
            // A replaced cart section begins a fresh bounded recovery cycle.
            if (current.some(item => !item.hasAttribute('data-cart-image-loader-row'))) {
              exhausted = false;
              recoveryStarted = 0;
              current.forEach(item => item.setAttribute('data-cart-image-loader-row', ''));
            }
            checkState();
          }, 100);
        });
        observer.observe(root, { childList: true, subtree: true });
      }
    }

    scan().length > 0 ? scheduleCheck(0) : checkState();
  };

  const stop = () => {
    started = false;
    stopTimer();
    if (observer) observer.disconnect();
    window.clearTimeout(observerTimer);
    observerTimer = null;
    observer = null;
  };

  window.CartImageLoader = {
    config,
    start,
    stop,
    checkNow: getCartData,
    scan
  };

  if (config.autoStart) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  }
})(window, document);
